'use client';

import type {
  ArticlePreview,
  IArticle,
  IBlogProps,
} from '@dealerportal/common';
import { formatDateAsUTC, isValidURL } from '@dealerportal/utilities';
import truncate from 'lodash/truncate';
import { ArticleTag } from './ArticleTag';
import { ReadTime } from './ReadTime';
import { RecentPost } from './RecentPost';

export type BlogProps = {
  blog: IBlogProps;
};

export function Blog(props: BlogProps) {
  const articles = props.blog.articlePreviews;
  return (
    <div className="ui-container !ui-max-w-screen-xl ui-pb-16">
      <h1 className="!ui-mb-6 !ui-text-3xl !ui-text-left">
        {props.blog.title}
      </h1>

      <div className="ui-grid ui-gap-12 lg:ui-grid-cols-3 ui-grid-cols-1">
        <div className="blog ui-min-w-0 lg:ui-order-1 ui-order-2 lg:ui-col-span-2 ui-grid ui-gap-12">
          {articles.length === 0 &&
            'No articles available at this time, please check back later.'}
          {articles.map((article: IArticle) => {
            const featuredImage = article.FeaturedImage;
            // De-duplicate and remove empty tags
            const articleTags = Array.from(
              new Set(
                article.ArticleTags?.filter(
                  (tag) => tag != null && /^\s+$/.test(tag) === false
                )
              )
            ).sort();

            const formattedDate = formatDateAsUTC(
              article?.PublishDate || new Date().toISOString()
            );

            const summary = article?.Summary;

            return (
              <a
                key={article.ArticleName}
                href={`/${props.blog.slug}/articles/${article.Slug}`}
                className="blog__article ui-min-w-0 !ui-text-black hover:!ui-text-black !ui-no-underline hover:!ui-no-underline"
              >
                <article>
                  {featuredImage && isValidURL(featuredImage.ImageURL) && (
                    <div className="blog__img ui-aspect-square md:ui-aspect-[12/4] ui-overflow-hidden">
                      <img
                        className="!ui-relative !ui-max-w-full ui-aspect-square md:ui-aspect-[12/4] ui-object-cover ui-object-center ui-rounded-md ui-w-full ui-h-auto"
                        src={featuredImage?.ImageURL}
                        alt={
                          featuredImage?.ImageAltText ??
                          'Article Featured Image'
                        }
                        {...featuredImage?.ImageMetadata}
                      />
                    </div>
                  )}

                  <div className="ui-p-2 ui-pt-0">
                    <div className="blog__info ui-text-sm ui-text-gray-600 ui-font-normal ui-mt-3">
                      <div className="ui-flex ui-items-center ui-gap-2">
                        <time dateTime={article.PublishDate}>
                          {formattedDate}
                        </time>
                        <span>-</span>
                        <ReadTime markup={article.Markup} />
                      </div>
                    </div>

                    <div className="ui-mt-3">
                      <h2 className="blog__heading !ui-m-0 !ui-text-left !ui-text-3xl">
                        {article.Heading}
                      </h2>

                      <p
                        className="blog__summary !ui-mt-3 ui-font-normal"
                        dangerouslySetInnerHTML={{
                          __html: truncate(summary?.replace('...', ''), {
                            length: 245,
                            separator: ' ',
                          }),
                        }}
                      />
                    </div>

                    {article.ArticleTags && (
                      <div className="blog__tags ui-mt-6 ui-flex ui-items-center ui-gap-2 ui-flex-wrap">
                        {articleTags.map((tag: string) => {
                          return (
                            <ArticleTag
                              key={tag}
                              blogSlug={props.blog.slug}
                              tag={tag}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                </article>
              </a>
            );
          })}
        </div>
        {articles.length !== 0 && (
          <aside className="aside lg:ui-order-2 ui-order-1">
            <div className="aside__heading lg:ui-ml-2 ui-text-lg ui-font-semibold">
              Recent Posts
            </div>
            {props.blog.recentPosts.map((item: ArticlePreview) => (
              <RecentPost
                key={item.ArticleName}
                article={item}
                blogSlug={props.blog.slug}
              />
            ))}
          </aside>
        )}
      </div>
    </div>
  );
}
